<style lang="scss" scoped>
.product-card {
    border-radius: 4px;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    background-color: #f6f6f8;

    .image-padding {
        padding: 1px 1px 0 1px;
    }

    .image-wrapper {
        width: 100%;
        padding-top: 75%; // 3/4
        position: relative;
        overflow: hidden;
        border-radius: 4px 4px 0 0;
        background-color: #fff;

        img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            transition: transform 0.2s;
        }
    }

    .content-wrapper {
        padding: 8px;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        @media screen and (min-width: 1024px) {
            padding: 16px;
        }

        .top-content {
            display: flex;
            flex-direction: column;
        }

        h3 {
            color: #1b1c1f;
            font-size: 0.875rem;
            font-weight: 700;
            line-height: 1.125rem;
            margin-bottom: 8px;

            @media screen and (min-width: 1024px) {
                font-size: 1.25rem;
                line-height: 1.625rem;
            }
        }

        .description {
            opacity: 0.6;
            color: #0e1935;
            font-size: 0.75rem;
            line-height: 1.25rem;
            margin-bottom: 8px;

            @media screen and (min-width: 1024px) {
                font-size: 0.875rem;
                line-height: 1.5rem;
                margin-bottom: 16px;
            }
        }

        .pricing {
            display: flex;
            align-items: flex-start;
            flex-direction: column;

            span {
                color: #acacac;
                font-size: 0.75rem;
                line-height: 1.125rem;
            }

            span.price {
                color: #2b5182;
                font-size: 1.25rem;
                font-weight: 700;
                letter-spacing: 0;
                line-height: 1.625rem;
                margin-right: 8px;

                @media screen and (min-width: 1024px) {
                    font-size: 1.5rem;
                    line-height: 2.3125rem;
                }
            }
            span.price-secondary {
                color: #acacac;
                font-size: 12px;
                letter-spacing: 0;
                line-height: 18px;
            }
        }
    }

    &:hover {
        img {
            transform: scale(1.1);
        }
    }

    .top-label {
        position: absolute;
        top: 8px;
        right: 8px;
        background-color: #2b5182;
        border-radius: 2px;
        color: #fff;
        font-size: 1rem;
        font-weight: 700;
        padding: 4px 8px;
    }
}
</style>

<template>
    <a v-if="translations" class="product-card" :href="link">
        <div class="image-padding">
            <div class="image-wrapper">
                <img class="lazyload" :data-src="firstImage" :alt="title" />
                <span v-if="translations && isSecondHand" class="absolute bottom-[8px] rounded-[2px] right-8 leading-5 bg-gray-200 text-14 font-bold text-gray-500 px-4">{{ translations.second_hand }}</span>
                <span class="top-label" v-if="translations && isTop">{{ translations.is_top }}</span>
            </div>
        </div>
        <div class="content-wrapper">
            <div class="top-content">
                <h3>{{ title }}</h3>
                <template v-if="translations">
                    <div class="inline-flex items-center pb-8" v-if="variantsInStock">
                        <span class="text-12 inline-block mr-4 text-green" v-html="svg('check-circle', 'fill-[currentColor]')"></span>
                        <span class="text-12 sm:text-14 text-green font-bold">{{ translations.in_stock }}</span>
                    </div>
                    <div class="inline-flex items-center pb-8" v-else>
                        <span class="text-12 inline-block mr-4 text-gray-500" v-html="svg('x-circle', 'fill-[currentColor]')"></span>
                        <span class="text-12 sm:text-14 text-gray-500 font-bold">{{ translations.not_in_stock }}</span>
                    </div>
                </template>
                <div class="description" v-html="description"></div>
            </div>
            <div class="pricing">
                <span>vanaf</span>
                <div>
                    <span class="price">€ {{ getLowestPrice($store.getters['indupackStore/pricesIncludingVat']) }}</span>
                    <span v-if="$store.getters['indupackStore/pricesIncludingVat']">{{ translations.vat_included_short }}</span>
                    <span v-else>{{ translations.vat_excluded_short }}</span>
                </div>
                <div>
                    <span class="price-secondary">€ {{ getLowestPrice(!$store.getters['indupackStore/pricesIncludingVat']) }}</span>
                    <span v-if="!$store.getters['indupackStore/pricesIncludingVat']">{{ translations.vat_included_short }}</span>
                    <span v-else>{{ translations.vat_excluded_short }}</span>
                </div>
            </div>
        </div>
    </a>
</template>

<script>
import cardBase from '@mixins/components/card-base';
import { getAvailableVariantStock } from '@modules/product-helper';

export default {
    name: 'Cards.ProductCard',

    mixins: [cardBase],

    props: {
        description: {
            type: String,
        },

        variants: {
            type: Array,
            required: true,
        },

        media: {
            type: Array,
            required: true,
        },
    },

    methods: {
        getLowestPrice(vat) {
            let lowestPrice = null;

            if (this.variants.length > 0) {
                if (this.variants[0].volume_prices != null && this.variants[0].volume_prices.length > 0) {
                    lowestPrice = vat ? this.variants[0].volume_prices[0].price_vat_incl : this.variants[0].volume_prices[0].price;

                    this.variants.forEach(v => {
                        v.volume_prices.forEach(vp => {
                            const vp_price = vat ? vp.price_vat_incl : vp.price;
                            if (lowestPrice > vp_price) {
                                lowestPrice = vp_price;
                            }
                        });
                    });
                } else {
                    lowestPrice = vat ? this.variants[0].price_vat_incl : this.variants[0].price;
                    this.variants.forEach(v => {
                        const v_price = vat ? v.price_vat_incl : v.price;
                        if (lowestPrice > v_price) {
                            lowestPrice = v_price;
                        }
                    });
                }

                let val = (lowestPrice / 1).toFixed(2).replace('.', ',');
                return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            }

            return lowestPrice;
        },
    },

    computed: {
        firstImage() {
            let image = '';
            if (this.media != null && this.media.length > 0) {
                this.media.forEach(m => {
                    if (m.type.indexOf('link') < 0 && image == '') {
                        image = m.path;
                    }
                });
            }
            return image;
        },

        variantsInStock() {
            return this.variants.some(variant => {
                return getAvailableVariantStock(variant) > 0;
            });
        },

        isSecondHand() {
            return this.variants.some(variant => {
                return variant.content.used;
            });
        },

        translations() {
            return this.$store.getters['general/generalData'].product_details;
        },
    },
};
</script>
